import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Database, PlayCircle, ShieldAlert, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const realtimeVideo = { url: "/videos/banetech-realtime-vs-legacy-batch.mp4" };

type Mode = "batch" | "realtime";

const SOURCES = ["Orders DB", "CRM", "Billing"];
const TARGETS = ["ERP", "Analytics", "APIs"];

const metrics: Record<Mode, { Icon: typeof Clock; label: string; value: string; note: string }[]> = {
  batch: [
    { Icon: Clock, label: "Data freshness", value: "Hours old", note: "Stale between scheduled runs" },
    { Icon: Database, label: "Source load", value: "Nightly spikes", note: "Full-table extracts hit production" },
    { Icon: ShieldAlert, label: "Failure impact", value: "Whole batch", note: "One bad record can stop the run" },
  ],
  realtime: [
    { Icon: Clock, label: "Data freshness", value: "Moments old", note: "Changes flow as they happen" },
    { Icon: Database, label: "Source load", value: "Low & steady", note: "CDC reads the transaction log" },
    { Icon: ShieldAlert, label: "Failure impact", value: "Single event", note: "Isolated and retried individually" },
  ],
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
};

export const RealTimeVsBatch = () => {
  const [mode, setMode] = useState<Mode>("realtime");
  const reduced = usePrefersReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: "100px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // Simulated clock: one tick = 15 simulated minutes. Runs only while on screen.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (reduced || !visible) return;
    const id = window.setInterval(() => setTick((t) => (t + 1) % 96), 200);
    return () => window.clearInterval(id);
  }, [reduced, visible]);

  const hour = Math.floor(tick / 4);
  const minute = (tick % 4) * 15;
  const clock = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  const batchRunning = hour === 2;
  const queued = batchRunning ? 0 : ((tick - 12 + 96) % 96);

  // Load history bars (last 24 points)
  const bars = Array.from({ length: 24 }, (_, i) => {
    const t = (tick - 23 + i + 96) % 96;
    const h = Math.floor(t / 4);
    if (mode === "batch") return h === 2 ? 95 : 8;
    return 22 + ((t * 37) % 9);
  });

  const isBatch = mode === "batch";

  return (
    <section ref={sectionRef} id="real-time" className="relative py-24 md:py-32 bg-surface/40 border-y border-border">
      <div className="container">
        <div className="max-w-2xl mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">The real-time advantage</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">Real-Time vs. Legacy Batch.</h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Scheduled batch jobs move data in heavy, periodic blocks. Event-driven integration and change data capture move each change as it happens. Switch modes to see the difference.
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={() => (window as unknown as { umami?: { track: (e: string) => void } }).umami?.track("realtime-video-play")}
                className="mt-6 inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-background px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
              >
                <PlayCircle className="h-4 w-4" /> Watch the 40s video
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl p-0 overflow-hidden">
              <DialogTitle className="sr-only">Real-Time vs. Legacy Batch video</DialogTitle>
              <video src={realtimeVideo.url} controls autoPlay playsInline className="w-full aspect-video bg-foreground" />
            </DialogContent>
          </Dialog>
        </div>

        <div role="tablist" aria-label="Integration mode" className="inline-flex rounded-xl border border-border bg-background p-1 mb-8">
          {(["batch", "realtime"] as Mode[]).map((m) => (
            <button
              key={m}
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {m === "batch" ? "Legacy batch ETL" : "Bane Tech real-time"}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-border bg-background p-6">
            <div className="flex items-center justify-between mb-4 font-mono text-xs text-muted-foreground">
              <span>Simulated day · {clock}</span>
              <span className={isBatch && batchRunning ? "text-destructive" : isBatch ? "" : "text-primary"}>
                {isBatch ? (batchRunning ? "● Batch window running" : `● ${queued} changes waiting`) : "● Streaming continuously"}
              </span>
            </div>

            <svg viewBox="0 0 600 260" className="w-full h-auto" role="img" aria-label={isBatch ? "Batch data flow" : "Real-time data flow"}>
              {SOURCES.map((s, i) => (
                <g key={s}>
                  <rect x="10" y={30 + i * 80} width="120" height="44" rx="10" className="fill-surface stroke-border" />
                  <text x="70" y={57 + i * 80} textAnchor="middle" className="fill-foreground text-[13px] font-medium">{s}</text>
                  <path d={`M130 ${52 + i * 80} C 190 ${52 + i * 80}, 210 130, 250 130`} className="stroke-border fill-none" strokeWidth="2" />
                </g>
              ))}
              {TARGETS.map((t, i) => (
                <g key={t}>
                  <rect x="470" y={30 + i * 80} width="120" height="44" rx="10" className="fill-surface stroke-border" />
                  <text x="530" y={57 + i * 80} textAnchor="middle" className="fill-foreground text-[13px] font-medium">{t}</text>
                  <path d={`M350 130 C 390 130, 410 ${52 + i * 80}, 470 ${52 + i * 80}`} className="stroke-border fill-none" strokeWidth="2" />
                </g>
              ))}
              <rect x="250" y="100" width="100" height="60" rx="12" className={isBatch ? "fill-muted stroke-border" : "fill-primary/10 stroke-primary"} strokeWidth="2" />
              <text x="300" y="127" textAnchor="middle" className="fill-foreground text-[12px] font-semibold">{isBatch ? "ETL job" : "CDC stream"}</text>
              <text x="300" y="145" textAnchor="middle" className="fill-muted-foreground text-[10px]">{isBatch ? "02:00 window" : "log-based"}</text>

              {!reduced && visible && !isBatch &&
                SOURCES.map((_, i) =>
                  [0, 1, 2].map((k) => (
                    <circle key={`${i}-${k}`} r="4" className="fill-primary">
                      <animateMotion dur="2.4s" begin={`${k * 0.8 + i * 0.25}s`} repeatCount="indefinite"
                        path={`M130 ${52 + i * 80} C 190 ${52 + i * 80}, 210 130, 250 130 L350 130 C 390 130, 410 ${52 + i * 80}, 470 ${52 + i * 80}`} />
                    </circle>
                  )),
                )}
              {!reduced && visible && isBatch && batchRunning &&
                SOURCES.map((_, i) => (
                  <rect key={i} width="18" height="12" rx="2" className="fill-destructive/80">
                    <animateMotion dur="1.4s" begin={`${i * 0.3}s`} repeatCount="indefinite"
                      path={`M130 ${52 + i * 80} C 190 ${52 + i * 80}, 210 130, 250 130 L350 130 C 390 130, 410 ${52 + i * 80}, 470 ${52 + i * 80}`} />
                  </rect>
                ))}
              {isBatch && !batchRunning &&
                Array.from({ length: Math.min(8, Math.ceil(queued / 10)) }, (_, k) => (
                  <rect key={k} x={140 + (k % 4) * 14} y={190 + Math.floor(k / 4) * 14} width="10" height="10" rx="2" className="fill-accent/60" />
                ))}
            </svg>

            <div className="mt-4">
              <div className="flex justify-between font-mono text-[11px] text-muted-foreground mb-2">
                <span>Source database load</span>
                <span>{isBatch ? (batchRunning ? "Peak I/O" : "Idle") : "Steady"}</span>
              </div>
              <div className="flex items-end gap-1 h-16">
                {bars.map((b, i) => (
                  <div key={i} className={`flex-1 rounded-sm transition-all duration-300 ${b > 80 ? "bg-destructive" : isBatch ? "bg-muted-foreground/30" : "bg-primary/60"}`} style={{ height: `${b}%` }} />
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {metrics[mode].map(({ Icon, label, value, note }) => (
              <div key={label} className="rounded-2xl border border-border bg-background p-5">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} /> {label}
                </div>
                <div className={`mt-2 font-display text-2xl font-semibold ${isBatch ? "text-foreground" : "text-primary"}`}>{value}</div>
                <p className="text-sm text-muted-foreground">{note}</p>
              </div>
            ))}
            <Link to="/resources/what-is-change-data-capture-cdc/" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
              <Zap className="h-4 w-4" /> Read: What Is Change Data Capture? <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
